import { Component, type ErrorInfo, type ReactNode } from 'react';
import { trackException } from '../../telemetry';
import { clearCrashReloads, nextCrashReload } from '../../lib/crashReload';

/** The crash boundary of a ring screen that nobody attends: the ring display
 *  (a TV) and the broadcast overlay (#604, #608). After a crash, it shows
 *  nothing and loads the page again. The wait is 10 s, then 30 s, 2 min and
 *  5 min for each next crash (lib/crashReload.ts). The wait returns to 10 s
 *  after one minute with no crash. `context` names the page in the telemetry. */
export default class SilentBoundary extends Component<{ context: 'RingDisplay' | 'RingOverlay'; children: ReactNode }, { failed: boolean }> {
  state = { failed: false };
  private noCrashTimer: number | undefined;
  static getDerivedStateFromError() { return { failed: true }; }
  componentDidMount() {
    this.noCrashTimer = window.setTimeout(() => { if (!this.state.failed) clearCrashReloads(); }, 60_000);
  }
  componentWillUnmount() { window.clearTimeout(this.noCrashTimer); }
  componentDidCatch(error: Error, info: ErrorInfo) {
    // The code schedules the reload first. The reload is the only way to
    // recover a page that no person watches. The record keeps the component
    // stack, as the global boundary of App.tsx does.
    window.setTimeout(() => window.location.reload(), nextCrashReload());
    trackException(error, { context: `${this.props.context}:crash`, componentStack: info.componentStack || '' });
  }
  render() { return this.state.failed ? null : this.props.children; }
}
